import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Helmet from '../../../components/Helmet';
import { createContactUsModal } from '../../../components/ContactUs';
import { overview } from './data/cn/overview';
import { scenes } from './data/cn/scenes';
import { SRM_OVERVIEW_TDK } from './seo-tdk';
import useCeilingTheme from './hooks/useCeilingTheme';
import useReveal from './hooks/useReveal';
// 样式直接取自 demo（AI-SRM-market.html），由 scripts/extract-demo-css.mjs 生成
import './styles/demo-overview.css';
import './styles/overrides.css';

const ROOT = '.srm-overview-page';

/** 首屏四个特性卡的图标，取自 demo hero-feature > svg */
const HERO_ICONS = [
  '<rect x="3.5" y="4.5" width="17" height="13" rx="2"/><path d="M7 14l3-3 2.5 2 4.5-5M8 20h8"/>',
  '<circle cx="6" cy="12" r="2.5"/><circle cx="17" cy="6" r="2.5"/><circle cx="17" cy="18" r="2.5"/><path d="M8.2 10.8l6.6-3.6M8.2 13.2l6.6 3.6"/>',
  '<path d="M12 3.5l7 3v5.2c0 4.2-2.9 7.2-7 8.8-4.1-1.6-7-4.6-7-8.8V6.5l7-3z"/><path d="M9 12l2 2 4-4"/>',
  '<path d="M4 16l5-5 3 3 7-7"/><path d="M14 7h5v5"/>'
];

/** 把「生长」二字做强调，还原 demo 的 <em> */
function renderHeroLine(line: string) {
  const keyword = '生长';
  const index = line.indexOf(keyword);
  if (index < 0) return line;
  return (
    <>
      {line.slice(0, index)}
      <em>{keyword}</em>
      {line.slice(index + keyword.length)}
    </>
  );
}

export default function AiSrmOverview() {
  const { hero, challenge, blueprint, capability, cases, cta } = overview;

  useCeilingTheme('.hero');
  useReveal(ROOT);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <div className="srm-overview-page">
      <Helmet
        title={SRM_OVERVIEW_TDK.title}
        description={SRM_OVERVIEW_TDK.description}
        keywords={SRM_OVERVIEW_TDK.keywords}
      />

      <main>
        {/* 首屏 */}
        <section className="hero">
          <img className="hero-bg" src={hero.background.url} alt={hero.background.alt} />
          <div className="hero-screen-motion" aria-hidden="true">
            <i />
            <i />
            <i />
            <b />
            <b />
            <b />
          </div>
          <div className="wrap">
            <div className="hero-copy reveal">
              <h1>
                {hero.titleLines.map((line, index) => (
                  <span className="hero-line" key={line}>
                    {index === 0 ? line : renderHeroLine(line)}
                  </span>
                ))}
              </h1>
              <p className="lead">{hero.lead}</p>

              <div className="hero-features">
                {hero.features.map((feature, index) => (
                  <div className="hero-feature" key={feature.title}>
                    <span className="hero-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" dangerouslySetInnerHTML={{ __html: HERO_ICONS[index] }} />
                    </span>
                    <b>{feature.title}</b>
                    <span>{feature.desc}</span>
                  </div>
                ))}
              </div>

              <div className="hero-actions">
                <a
                  className="btn ghost consult-btn"
                  href="#capability"
                  onClick={(event) => {
                    event.preventDefault();
                    createContactUsModal();
                  }}
                >
                  {hero.cta}
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* 业务挑战 */}
        <section className="section problem" id="value">
          <div className="wrap">
            <div className="section-head reveal">
              <div>
                <h2>{challenge.title}</h2>
              </div>
              <p>{challenge.desc}</p>
            </div>
            <div className="problem-layout">
              <figure className="problem-photo reveal">
                <img src={challenge.photo.url} alt={challenge.photo.alt} />
                <figcaption>
                  <span>{challenge.photoCaption.label}</span>
                  <b>{challenge.photoCaption.text}</b>
                </figcaption>
              </figure>
              <div className="challenge-grid reveal">
                {challenge.items.map((item) => (
                  <article className="challenge" key={item.num}>
                    <span className="num">{item.num}</span>
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 智慧供应链蓝图（demo 中该区块只显示整图） */}
        <section className="section procurement-blueprint" id="blueprint">
          <div className="wrap">
            <div className="blueprint-head reveal">
              <div>
                <h2>{blueprint.title}</h2>
                <p>{blueprint.desc}</p>
              </div>
            </div>
            <div className="blueprint-board blank-frame reveal" aria-label="采购供应链内容框">
              <img
                className="blueprint-supplychain-image"
                src={blueprint.image.url}
                alt={blueprint.image.alt}
              />
            </div>
          </div>
        </section>

        {/* 六大智慧场景 */}
        <section className="section scenario-showcase" id="capability">
          <div className="wrap">
            <div className="scenario-showcase-head reveal">
              <div>
                <h2>{capability.title}</h2>
              </div>
              <p>{capability.desc}</p>
            </div>
            <div className="scenario-card-grid reveal" aria-label="六大核心场景">
              {scenes.map((scene) => {
                const [lead, tail] = scene.cardTitle.split('·');
                return (
                  <article className="scenario-sap-card" key={scene.key}>
                    <div className="scenario-sap-copy">
                      <div className="scenario-sap-meta">
                        <b>
                          {lead}
                          {tail ? (
                            <span className="scenario-separator" aria-hidden="true">
                              ·
                            </span>
                          ) : null}
                          {tail}
                        </b>
                      </div>
                      <h3>{scene.subtitle}</h3>
                    </div>
                    <figure className={`scenario-sap-media ${scene.cardVariant}`}>
                      <img src={scene.cardImage.url} alt={scene.cardImage.alt} />
                      <figcaption className="scenario-ai-panel">
                        <div
                          className={`scenario-ai-list${scene.cardTags.length > 3 ? ' four' : ''}`}
                          aria-label={`${scene.cardTitle}智能场景`}
                        >
                          {scene.cardTags.map((tag) => (
                            <span key={tag}>{tag}</span>
                          ))}
                        </div>
                      </figcaption>
                    </figure>
                    <Link className="scenario-sap-link" to={`/product/ai-srm/scene/${scene.key}`}>
                      点击了解更多 <span>→</span>
                    </Link>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* 客户实践 */}
        <section className="section cases" id="cases">
          <div className="wrap">
            <div className="section-head reveal">
              <div>
                <h2>{cases.title}</h2>
              </div>
              <p>{cases.desc}</p>
            </div>
            <div className="case-grid">
              {cases.items.map((item) => (
                <a className="case reveal" href={item.href} key={item.name}>
                  <img className="case-image" src={item.image.url} alt={item.image.alt} />
                  <div className="case-body">
                    <h3>{item.name}</h3>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* 底部转化 */}
        <section className="section cta" id="contact">
          <div className="wrap">
            <div className="cta-box">
              <div className="cta-content">
                <h2>{cta.title}</h2>
                <p>{cta.desc}</p>
                <div className="cta-action">
                  <a
                    className="btn cta-primary"
                    href="#contact"
                    onClick={(event) => {
                      event.preventDefault();
                      createContactUsModal();
                    }}
                  >
                    {cta.primary}
                  </a>
                  <a className="btn cta-secondary" href="https://www.terminus.io/about-us/news">
                    {cta.secondary}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
